/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2026 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React from 'react'
import { Doughnut } from 'react-chartjs-2'
import { Chart as ChartJS, ArcElement, Tooltip } from 'chart.js'
import Styles from './styles.module.sass'
import { FaCircle } from 'react-icons/fa'

ChartJS.register(ArcElement, Tooltip)

const TopBuyersChart = ({ topBuyers, totalRevenue }) => {
  if (!topBuyers || topBuyers.length === 0) {
    return (
      <div className={Styles.chartContainer}>
        <div className="text-center py-12 text-gray-500">
          No buyers yet
        </div>
      </div>
    )
  }

  const labels = topBuyers.map(buyer =>
    buyer.user?.nickname || `User ${buyer.user?.id}` || 'Unknown'
  )

  const dataValues = topBuyers.map(buyer => buyer.percentage)
  const colors = [
    '#ee0c0c',
    '#f45a5a',
    '#fccece',
    '#ff9999',
    '#ffcccc'
  ]

  const backgroundColors = colors.slice(0, topBuyers.length)

  const data = {
    labels,
    datasets: [
      {
        data: dataValues,
        backgroundColor: backgroundColors,
        hoverBackgroundColor: backgroundColors,
        borderWidth: 20
      }
    ]
  }

  const plugins = [{
    id: 'centerText',
    beforeDraw: function (chart) {
      const width = chart.width
      const height = chart.height * 0.5
      const ctx = chart.ctx
      ctx.restore()

      const totalRevenueFormatted = `$${totalRevenue.toFixed(2)}`
      const totalRevenueLabel = 'Total Revenue'
      const fontSize = (height / 160).toFixed(2) * 20

      ctx.font = `bold ${fontSize}px sans-serif`
      ctx.textBaseline = 'top'
      ctx.font = `${fontSize}px sans-serif`

      const textX1 = Math.round((width - ctx.measureText(totalRevenueFormatted).width) / 2)
      const textX2 = Math.round((width - ctx.measureText(totalRevenueLabel).width) / 2)
      const textY1 = chart.height * 0.45
      const textY2 = textY1 + fontSize

      ctx.font = `bold ${fontSize}px sans-serif`
      ctx.fillText(totalRevenueFormatted, textX1, textY1)
      ctx.font = `${fontSize * 0.6}px sans-serif`
      ctx.fillText(totalRevenueLabel, textX2, textY2)
      ctx.save()
    }
  }]

  const options = {
    cutout: '70%',
    plugins: {
      legend: {
        display: false
      }
    }
  }

  return (
    <div className={Styles.chartContainer}>
      <Doughnut data={data} plugins={plugins} options={options} />

      { topBuyers.map((buyer, index) => {
        const iconStyle = {
          size: 12,
          color: backgroundColors[index]
        }
        const buyerName = buyer.user?.nickname ||
          `User ${buyer.user?.id}` ||
          'Unknown'

        return (
          <div key={index} className={Styles.labelsContainer}>
            <div className={Styles.iconContainer}>
              <FaCircle {...iconStyle} />
            </div>
            <div className={Styles.labelContainer}>
              <p title={buyer.user?.zetoniumId}>
                {buyerName}
              </p>
            </div>
            <div className={Styles.percentageContainer}>
              <p className="has-text-weight-semibold">
                {buyer.percentage.toFixed(1)}%
              </p>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default TopBuyersChart
